/**
 * LineKindPicker.jsx
 *
 * What KIND of thing a line is — set where the line is, which is the only place the question can be
 * answered.
 *
 * ⭐ WHY IT IS A ROW CONTROL. You find out that a line is a sign by READING it: "Ground Squirrel."
 * on its own, attributed to nobody, is a sign the moment you see it in context and a mystery in any
 * list that has taken it out of context. Same argument as the per-line re-render tick and the
 * pronunciation capture beside it — a fix that needs you to go somewhere else is a fix that mostly
 * does not happen.
 *
 * ⛔ THE VOCABULARY COMES FROM THE SERVER. The six kinds, their labels and their hints arrive with
 * the kind-voices payload rather than being listed here, because a second copy of a closed table is
 * a second copy that drifts — which is the whole reason `renderableKinds.js` exists on the server.
 * No table loaded, no control: better to show nothing than to offer a menu of guesses.
 *
 * ⚠️ A KIND IS NOT A SPEAKER. Setting one says what the line IS, never who says it. If a line turns
 * out to be spoken by someone the book names, that is a reassignment; the kind then supplies only
 * the delivery. See docs/journal/UTTERANCE_KINDS_A_SIGN_IS_NOT_A_SPEAKER_SPEC.md.
 */

import React from "react";
import { Dropdown, Icon, Popup } from "semantic-ui-react";

const CLEAR = "__clear__";

/** Build the menu once per render from the server's table. */
const optionsFor = (kinds) => ([
  { key: CLEAR, value: CLEAR, text: "— no kind —" },
  ...(kinds || []).map((k) => ({
    key: k.kind,
    value: k.kind,
    text: k.label,
    // The voice, in the menu, at the moment of choosing — so "Signage" reads as a working decision
    // or as half of one without the operator having to remember which kinds they have voiced.
    description: k.characterName ? k.characterName : "no voice",
  })),
]);

/**
 * The inline control on a line-set row. Compact and text-free when nothing is set, so 150 rows of
 * ordinary dialogue stay readable and the marked ones stand out.
 */
export const LineKindCell = ({ row, kinds, onSetKind, busy }) => {
  if (!kinds || !kinds.length || !onSetKind) return null;
  const current = row.utteranceKind || null;
  const entry = current ? (kinds.find((k) => k.kind === current) || null) : null;
  // ⚠️ `row.kindVoice` is the name resolved at BOOT, so it already accounts for a voice whose
  // character has since been merged away — which is why a marked row can truthfully say "no voice"
  // while the panel above still shows a choice.
  const voice = row.kindVoice || (entry ? entry.characterName : null);

  /**
   * ─── ⛔ THE KIND'S VOICE IS OUTRANKED — BUT ITS DELIVERY IS NOT ────────────────────────────────
   *
   * A kind lends its voice only to a character that has NONE: the rung sits below the character's own
   * voice. So on a cast speaker's line the kind's VOICE will not be heard.
   *
   * ⭐ THE STATE THIS EXISTS FOR, and it cost the operator an evening: "Humbecker Ford 1.8 miles."
   * marked `signage` — right, it is a waysign, not Father-Mayor Gullwimple speaking — reported applied,
   * and still read in Gullwimple's voice. The chip said "Signage" and looked handled.
   *
   * ⚠️ THIS ONCE SAID THE KIND "changes nothing about its sound", AND THAT WAS WRONG — it talked the
   * operator out of a correct setting on 2026-10-03 (`announcement` on Romundo's "CHIEF ON THE
   * BRIDGE"). `ScriptLinker._materialize` reads the kind from `kindByLineId` for the LINE, not from
   * whichever rung resolved the voice, and it runs on the character paths too: the kind's
   * `renderTemplate` and `voiceSettings` are applied wherever the matched variation does not set its
   * own. Measured on Romundo — every one of his variations has `renderTemplate=null` and
   * `voiceSettings=null`, so `announcement` would have delivered its `[firmly]` and stability 0.65.
   *
   * ⛔ So the honest split is VOICE vs DELIVERY, and `hasTreatment` decides the second — `recording`
   * declares no treatment on purpose and really is audibly inert.
   *
   * ⚠️ `=== true`, because `null` means the server could not tell. "We do not know" must not be drawn
   * as a warning; only a measured yes.
   */
  const outranked = !!current && row.speakerHasOwnVoice === true;
  /**
   * ⛔ Outranked AND audibly inert — the only state that earns red. `hasTreatment === false` is
   * `recording`, which declares no delivery by design; `undefined` (an older payload) is treated as
   * "it may deliver", because overstating inertness is the mistake this whole change undoes.
   */
  const inert = outranked && entry && entry.hasTreatment === false;

  return (
    <Dropdown
      className="rud-kind-picker"
      // `inline` keeps the row height unchanged; the trigger carries the whole state.
      inline
      disabled={!!busy}
      options={optionsFor(kinds)}
      value={current || CLEAR}
      onChange={(_, d) => onSetKind([row.lineId], d.value === CLEAR ? null : d.value)}
      trigger={
        current ? (
          <Popup
            size={outranked ? "small" : "mini"}
            wide={outranked ? "very" : undefined}
            content={outranked ? (
              // ⛔ The one state that needs a sentence rather than a label: the decision is right, the
              // effect is nil, and the way out is a DIFFERENT tool. Naming both routes, because
              // reassigning and borrowing a voice have different consequences for the cast list.
              <div style={{ fontSize: "14px", lineHeight: 1.45 }}>
                <div style={{ marginBottom: ".4em" }}>
                  <strong>{entry ? entry.label : current}</strong> is recorded, but its <strong>voice</strong>{" "}
                  will not be heard: <strong>{row.speaker || "this speaker"}</strong> has a voice of their
                  own, and a kind only lends its voice to a character that has none.
                </div>
                {/* ⭐ The correction: a kind with a treatment is NOT inert on a cast speaker's line. */}
                <div style={{ marginBottom: ".4em" }}>
                  {entry && entry.hasTreatment === false ? (
                    <>This kind declares no delivery of its own, so nothing about the sound changes.</>
                  ) : (
                    <>
                      Its <strong>delivery</strong> still applies — the kind&apos;s render tag and voice
                      settings are used wherever {row.speaker || "the speaker"}&apos;s own variation does
                      not set them. Marking the form is not wasted.
                    </>
                  )}
                </div>
                <div style={{ opacity: 0.85 }}>
                  To give the line the kind&apos;s <strong>voice</strong> as well — reassign it to an
                  uncast label (moves the attribution), or set <strong>Voice of</strong> on it (keeps the
                  attribution, borrows only the audio).
                </div>
              </div>
            ) : (voice
              ? `${entry ? entry.label : current} — read by ${voice}.`
              : `${entry ? entry.label : current} — no voice yet, so this changes nothing. `
                + `Choose who reads it in "Who reads what kind".`)}
            trigger={
              // ⭐ The Popup's trigger spans the whole cell, so the tooltip appears wherever the
              // pointer is rather than only over a 12px chip. The chip inside keeps its own size —
              // see the click-zone note in RenderingStudio.css.
              <span className="rud-cell-trigger">
                <span className={`rud-kind-chip${outranked ? (inert ? " rud-kind-chip-outranked" : " rud-kind-chip-delivery-only") : (voice ? "" : " rud-kind-chip-unvoiced")}`}>
                  {entry ? entry.label : current}
                  {/* ⛔ `ban` only for the genuinely inert case. An outranked kind that still delivers
                      gets the same amber warning as `unvoiced`: partial, not forbidden. */}
                  {outranked && inert
                    ? <Icon name="ban" color="red" size="small" style={{ marginLeft: ".3em" }} />
                    : ((outranked || !voice)
                      && <Icon name="warning sign" color="orange" size="small" style={{ marginLeft: ".3em" }} />)}
                </span>
              </span>
            }
          />
        ) : (
          <Popup
            size="mini"
            content="Mark what kind of thing this line is — a sign, a tannoy, a thought. For lines nobody speaks."
            trigger={
              <span className="rud-cell-trigger">
                <span className="rud-kind-chip rud-kind-chip-empty">—</span>
              </span>
            }
          />
        )
      }
    />
  );
};

/**
 * The same choice for every ticked line at once.
 *
 * ⭐ THE REASON THE ENDPOINT TAKES AN ARRAY. A book's signage arrives in clumps — a street, a
 * terminal, a corridor of doors — and marking 23 lines one at a time is how a sweep gets abandoned
 * half-done. The button reports what it actually changed, because a lineId belonging to another book
 * matches nothing and "requested 23, updated 21" is the only way to find that out.
 */
export const LineKindBulkPicker = ({ kinds, count, onSetKind, busy }) => {
  if (!kinds || !kinds.length || !onSetKind || !count) return null;
  return (
    <Dropdown
      button
      basic
      compact
      className="icon rud-kind-bulk"
      icon="tag"
      floating
      labeled
      disabled={!!busy}
      loading={!!busy}
      text={`Set kind (${count})`}
      options={optionsFor(kinds)}
      value={null}
      selectOnBlur={false}
      onChange={(_, d) => onSetKind(null, d.value === CLEAR ? null : d.value)}
    />
  );
};

export default LineKindCell;
